// src/components/profile/Profile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import Toast from '../common/Toast';
import api from '../../services/api';

const Profile = () => {
    usePageTitle('My Profile');
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [profileData, setProfileData] = useState({
        username: '',
        email: '',
        name: '',
        user_type: ''
    });

    useEffect(() => {
        if (user) {
            setProfileData({
                username: user.username || '',
                email: user.email || '',
                name: user.name || '',
                user_type: user.user_type || ''
            });
        }
    }, [user]);

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
    };

    const getUserRoleDisplay = () => {
        switch (profileData.user_type) {
            case 'core_admin':
                return 'Core Admin';
            case 'client_admin':
                return 'Organization Admin';
            case 'app_admin':
                return 'Premises Admin';
            default:
                return 'Staff Member';
        }
    };

    const getUserIcon = () => {
        switch (profileData.user_type) {
            case 'core_admin':
                return 'fas fa-crown';
            case 'client_admin':
                return 'fas fa-user-tie';
            case 'app_admin':
                return 'fas fa-user-cog';
            default:
                return 'fas fa-user';
        }
    };

    return (
        <div className="profile-container">
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: '' })}
            />

            <div className="profile-header">
                <div>
                    <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                        My Profile
                    </h1>
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                        View and manage your account information
                    </p>
                </div>
            </div>

            <div className="profile-content">
                <div className="profile-card">
                    <div className="profile-card-header">
                        <h2>
                            <i className="fas fa-user-circle"></i>
                            Personal Information
                        </h2>
                    </div>
                    <div className="profile-card-body">
                        <div className="profile-info-grid">
                            <div className="profile-info-item">
                                <label>Username</label>
                                <div className="profile-info-value">
                                    <i className="fas fa-user"></i>
                                    {profileData.username}
                                </div>
                            </div>

                            <div className="profile-info-item">
                                <label>Email Address</label>
                                <div className="profile-info-value">
                                    <i className="fas fa-envelope"></i>
                                    {profileData.email}
                                </div>
                            </div>

                            <div className="profile-info-item">
                                <label>Full Name</label>
                                <div className="profile-info-value">
                                    <i className="fas fa-id-card"></i>
                                    {profileData.name || 'Not provided'}
                                </div>
                            </div>

                            <div className="profile-info-item">
                                <label>User Role</label>
                                <div className="profile-info-value">
                                    <i className={getUserIcon()}></i>
                                    {getUserRoleDisplay()}
                                </div>
                            </div>

                            {user?.org_name && (
                                <div className="profile-info-item">
                                    <label>Organization</label>
                                    <div className="profile-info-value">
                                        <i className="fas fa-building"></i>
                                        {user.org_name}
                                    </div>
                                </div>
                            )}

                            {user?.premises_name && (
                                <div className="profile-info-item">
                                    <label>Premises</label>
                                    <div className="profile-info-value">
                                        <i className="fas fa-map-marker-alt"></i>
                                        {user.premises_name}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="profile-card">
                    <div className="profile-card-header">
                        <h2>
                            <i className="fas fa-shield-alt"></i>
                            Account Status
                        </h2>
                    </div>
                    <div className="profile-card-body">
                        <div className="status-items">
                            <div className="status-item">
                                <div className="status-icon verified">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <div className="status-info">
                                    <label>Email Verification</label>
                                    <p>{user?.email_verified_at ? 'Verified' : 'Not Verified'}</p>
                                </div>
                            </div>

                            <div className="status-item">
                                <div className="status-icon active">
                                    <i className="fas fa-user-check"></i>
                                </div>
                                <div className="status-info">
                                    <label>Account Status</label>
                                    <p>Active</p>
                                </div>
                            </div>

                            <div className="status-item">
                                <div className="status-icon role">
                                    <i className={getUserIcon()}></i>
                                </div>
                                <div className="status-info">
                                    <label>User Role</label>
                                    <p>{getUserRoleDisplay()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .profile-container {
                    padding: 20px;
                }

                .profile-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                }

                .profile-content {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    gap: 20px;
                }

                .profile-card {
                    background: #fff;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .profile-card-header {
                    background: #f8f9fa;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .profile-card-header h2 {
                    margin: 0;
                    font-size: 18px;
                    color: #333;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .profile-card-header h2 i {
                    color: #007bff;
                }

                .profile-card-body {
                    padding: 24px;
                }

                .profile-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 20px;
                }

                .profile-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .profile-info-item label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #666;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .profile-info-value {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    font-size: 14px;
                    color: #333;
                    padding: 12px 16px;
                    background: #f8f9fa;
                    border-radius: 6px;
                    border: 1px solid #e0e0e0;
                    min-height: 44px;
                }

                .profile-info-value i {
                    color: #007bff;
                    width: 16px;
                    text-align: center;
                }

                .status-items {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .status-item {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #e0e0e0;
                }

                .status-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                }

                .status-icon.verified {
                    background: rgba(40, 167, 69, 0.1);
                    color: #28a745;
                }

                .status-icon.active {
                    background: rgba(0, 123, 255, 0.1);
                    color: #007bff;
                }

                .status-icon.role {
                    background: rgba(102, 126, 234, 0.1);
                    color: #667eea;
                }

                .status-info {
                    flex: 1;
                }

                .status-info label {
                    display: block;
                    font-size: 12px;
                    font-weight: 600;
                    color: #666;
                    margin-bottom: 4px;
                }

                .status-info p {
                    margin: 0;
                    font-size: 15px;
                    color: #333;
                    font-weight: 500;
                }

                @media (max-width: 1024px) {
                    .profile-content {
                        grid-template-columns: 1fr;
                    }

                    .profile-info-grid {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 768px) {
                    .profile-container {
                        padding: 16px;
                    }

                    .profile-card-body {
                        padding: 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Profile;
